import { useState, useEffect, useMemo } from "react";
import './modal.css'
import { AllUsers, createInvoice, getInvoiceNumber } from "../api";

// Tax type options
const taxTypeOptions = [
  { value: 'gst', label: 'GST' },
  { value: 'vat', label: 'VAT' },
  { value: 'Professional_tax', label: 'Professional Tax' }
];

export default function InvoiceCreateModal({ isOpen, onClose, onCreate, providers }) {
  const [formData, setFormData] = useState({
    // Basic Info
    invoiceNo: "I-000001",
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: "",
    
    // Customer Info
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    customerAddress: "",
    customerGSTIN: "",
    receiverId: "",
    
    // Invoice Details
    items: [{
      description: "",
      quantity: 1,
      unitPrice: 0,
      taxRates: [], 
      amount: 0
    }],
    
    // Payment Info
    paymentTerms: "Net 30",
    currency: "INR",
    notes: "",
    
    // Integration
    sourceSystem: "internal",
    externalProvider: "",
    externalId: "",
    
    // Totals (calculated)
    subtotal: 0,
    tax: 0,
    total: 0
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allusers, setAllUsers] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    const apiHelper = async () => {
      try {
        const data = await getInvoiceNumber();
        console.log(data);
        
        setFormData(prev => ({
          ...prev,
          invoiceNo: data.invoiceNo
        }));
        
        const users = await AllUsers();
        console.log(users);
        
        setAllUsers(users?.data?.allUsers || [])
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
    
    if (isOpen) {
      apiHelper();
    }
  }, [isOpen]);

  // Calculate totals whenever items change
  useEffect(() => {
    calculateTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.items]);

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return allusers;
    
    return allusers.filter(user => {
      const searchLower = searchQuery.toLowerCase();
      return (
        (user?.profile?.name && user?.profile?.name.toLowerCase().includes(searchLower)) ||
        (user.email && user.email.toLowerCase().includes(searchLower))
      );
    });
  }, [allusers, searchQuery]);

  // Calculate totals for each item and overall
  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;
    
    formData.items.forEach(item => {
      const itemAmount = item.quantity * item.unitPrice;
      subtotal += itemAmount;
      
      // Calculate tax for this item based on selected tax rates
      let itemTax = 0;
      if (item.taxRates && item.taxRates.length > 0) {
        item.taxRates.forEach(tax => {
          if (tax.percentage && !isNaN(tax.percentage)) {
            itemTax += itemAmount * (parseFloat(tax.percentage) / 100);
          }
        });
      }
      totalTax += itemTax;
      
      // Update item amount with tax
      item.amount = itemAmount + itemTax;
    });

    setFormData(prev => ({
      ...prev,
      subtotal,
      tax: totalTax,
      total: subtotal + totalTax
    }));
  };

  // Handle tax type checkbox change for an item
  const handleTaxTypeChange = (itemIndex, taxType) => {
    const updatedItems = [...formData.items];
    const itemTaxRates = [...updatedItems[itemIndex].taxRates];
    const existingTaxIndex = itemTaxRates.findIndex(tax => tax.type === taxType);
    
    if (existingTaxIndex >= 0) {
      // Remove tax if it exists
      itemTaxRates.splice(existingTaxIndex, 1);
    } else {
      // Add new tax with default percentage
      itemTaxRates.push({
        type: taxType,
        percentage: ""
      });
    }
    
    updatedItems[itemIndex].taxRates = itemTaxRates;
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  // Handle tax percentage change for an item
  const handleTaxPercentageChange = (itemIndex, taxType, percentage) => {
    const updatedItems = [...formData.items];
    const updatedTaxRates = updatedItems[itemIndex].taxRates.map(tax => 
      tax.type === taxType 
        ? { ...tax, percentage: percentage } 
        : tax
    );
    
    updatedItems[itemIndex].taxRates = updatedTaxRates;
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  // Check if a tax type is selected for an item
  const isTaxTypeSelected = (itemIndex, taxType) => {
    return formData.items[itemIndex]?.taxRates?.some(tax => tax.type === taxType);
  };

  // Get tax percentage for a specific tax type in an item
  const getTaxPercentage = (itemIndex, taxType) => {
    const tax = formData.items[itemIndex]?.taxRates?.find(tax => tax.type === taxType);
    return tax ? tax.percentage : "";
  };

  // Calculate total tax percentage for an item
  const calculateItemTaxPercentage = (itemIndex) => {
    const item = formData.items[itemIndex];
    if (!item.taxRates || item.taxRates.length === 0) return 0;
    
    return item.taxRates.reduce((sum, tax) => {
      const percentage = parseFloat(tax.percentage) || 0;
      return sum + percentage;
    }, 0);
  };

  // Get tax label by value
  const getTaxLabel = (taxValue) => {
    const option = taxTypeOptions.find(option => option.value === taxValue);
    return option ? option.label : taxValue;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If customer name is being typed manually, clear selected user
    if (name === "customerName") {
      if (!value) {
        setFormData(prev => ({
          ...prev,
          customerName: "",
          customerEmail: "",
          customerPhone: "",
          customerAddress: "",
          customerGSTIN: "",
          receiverId: ""
        }));
        setSelectedUser(null);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  // Handle user selection from dropdown
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchQuery("");
    setShowUserDropdown(false);
    
    setFormData(prev => ({
      ...prev,
      customerName: user?.profile?.name || "",
      customerEmail: user.email || "",
      customerPhone: user.mobile || "",
      customerAddress: user?.profile?.location  || "",
      customerGSTIN: user.gstin || user.taxId || "",
      receiverId: user._id || user.id
    }));
  };

  // Clear selected user and all fields
  const clearUserSelection = () => {
    setSelectedUser(null);
    setSearchQuery("");
    setFormData(prev => ({
      ...prev,
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      customerAddress: "",
      customerGSTIN: "",
      receiverId: ""
    }));
  };

  // Handle item changes (description, quantity, unit price)
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    
    if (field === 'quantity' || field === 'unitPrice') {
      updatedItems[index][field] = Number(value);
    } else {
      updatedItems[index][field] = value;
    }
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  // Add new item
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        description: "",
        quantity: 1,
        unitPrice: 0,
        taxRates: [],
        amount: 0
      }]
    }));
  };

  // Remove item
  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const updatedItems = [...formData.items];
      updatedItems.splice(index, 1);
      setFormData(prev => ({
        ...prev,
        items: updatedItems
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.invoiceNo.trim()) newErrors.invoiceNo = "Invoice number is required";
    if (!formData.invoiceDate) newErrors.invoiceDate = "Invoice date is required";
    if (!formData.dueDate) newErrors.dueDate = "Due date is required";
    if (!formData.customerName.trim()) newErrors.customerName = "Customer name is required";
    if (!formData.customerEmail.trim()) newErrors.customerEmail = "Customer email is required";
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.customerEmail && !emailRegex.test(formData.customerEmail)) {
      newErrors.customerEmail = "Invalid email format";
    }

    // Phone validation (Indian format)
    if (!formData.customerPhone.trim()) {
      newErrors.customerPhone = "Invalid phone number";
    }

    // GST validation (if provided)
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (formData.customerGSTIN && !gstRegex.test(formData.customerGSTIN)) {
      newErrors.customerGSTIN = "Invalid GSTIN format";
    }

    // Validate items
    formData.items.forEach((item, index) => {
      if (!item.description.trim()) {
        newErrors[`item_description_${index}`] = "Item description is required";
      }
      if (item.quantity <= 0) {
        newErrors[`item_quantity_${index}`] = "Quantity must be greater than 0";
      }
      if (item.unitPrice < 0) {
        newErrors[`item_unitPrice_${index}`] = "Unit price cannot be negative";
      }
      
      // Validate tax percentages for selected taxes
      if (item.taxRates && item.taxRates.length > 0) {
        item.taxRates.forEach(tax => {
          if (!tax.percentage || isNaN(parseFloat(tax.percentage)) || parseFloat(tax.percentage) <= 0) {
            newErrors[`item_${index}_tax_${tax.type}`] = `${getTaxLabel(tax.type)} percentage must be greater than 0`;
          }
        });
      }
    });

    // Due date validation
    if (formData.invoiceDate && formData.dueDate) {
      const invoiceDate = new Date(formData.invoiceDate);
      const dueDate = new Date(formData.dueDate);
      if (dueDate < invoiceDate) {
        newErrors.dueDate = "Due date cannot be before invoice date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      alert("Please fix the errors in the form");
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare final data
      const invoiceData = {
        ...formData,
        status: "draft",
        createdAt: new Date().toISOString(),
        // Include receiverId
        receiverId: formData.receiverId || null,
        // Format items for backend
        items: formData.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxRates: item.taxRates || [],
          amount: item.amount
        }))
      };

      console.log("Submitting invoice data:", invoiceData);
      const data = await createInvoice(invoiceData);
      onCreate(data?.data);
      
      // Reset form and close modal
      resetForm();
      onClose();
      
    } catch (error) {
      console.error("Error creating invoice:", error);
      alert("Failed to create invoice. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      invoiceNo: "",
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: "",
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      customerAddress: "",
      customerGSTIN: "",
      receiverId: "",
      items: [{
        description: "",
        quantity: 1,
        unitPrice: 0,
        taxRates: [],
        amount: 0
      }],
      paymentTerms: "Net 30",
      currency: "INR",
      notes: "",
      sourceSystem: "internal",
      externalProvider: "",
      externalId: "",
      subtotal: 0,
      tax: 0,
      total: 0
    });
    setSelectedUser(null);
    setSearchQuery("");
    setErrors({});
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: formData.currency
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1002
    }}>
      <div className="modal" style={{
        background: 'white',
        padding: 24,
        borderRadius: 8,
        width: 950, // Increased width to accommodate tax columns
        maxWidth: '95vw',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0 }}>Create New Invoice</h2>
          <button 
            onClick={onClose}
            style={{ 
              background: 'none', 
              border: 'none', 
              fontSize: 24, 
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Invoice Header */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: 16,
            marginBottom: 24 
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Invoice Number *
              </label>
              <input
                readOnly
                type="text"
                name="invoiceNo"
                value={formData.invoiceNo}
                onChange={handleInputChange}
                placeholder="INV-2024-001"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${errors.invoiceNo ? '#f44336' : '#ddd'}`,
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
              {errors.invoiceNo && (
                <div style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>
                  {errors.invoiceNo}
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Invoice Date *
              </label>
              <input
                type="date"
                name="invoiceDate"
                value={formData.invoiceDate}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${errors.invoiceDate ? '#f44336' : '#ddd'}`,
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
              {errors.invoiceDate && (
                <div style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>
                  {errors.invoiceDate}
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Due Date *
              </label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: `1px solid ${errors.dueDate ? '#f44336' : '#ddd'}`,
                  borderRadius: 4,
                  fontSize: 14
                }}
              />
              {errors.dueDate && (
                <div style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>
                  {errors.dueDate}
                </div>
              )}
            </div>
          </div>

          {/* Customer Information */}
          <div style={{ 
            padding: 16, 
            background: '#f8f9fa', 
            borderRadius: 4,
            marginBottom: 24 
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 16 }}>Customer Information</h3>
            
            {/* Customer Search/Select */}
            
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            <div style={{ marginBottom: 16, position: 'relative' }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Select Customer
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  name="customerName"
                  value={formData.customerName}
                  onChange={(e) => {
                    handleInputChange(e);
                    setSearchQuery(e.target.value);
                    if (e.target.value && !selectedUser) {
                      setShowUserDropdown(true);
                    }
                  }}
                  onFocus={() => {
                    if (formData.customerName && !selectedUser) {
                      setShowUserDropdown(true);
                    }
                  }}
                  placeholder="Start typing to search customers..."
                  style={{
                    width: '100%',
                    padding: '8px 12px 8px 36px',
                    border: `1px solid ${errors.customerName ? '#f44336' : '#ddd'}`,
                    borderRadius: 4,
                    fontSize: 14
                  }}
                />
                <div style={{
                  position: 'absolute',
                  left: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666'
                }}>
                  🔍
                </div>
                {selectedUser && (
                  <button
                    type="button"
                    onClick={clearUserSelection}
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: '#f44336',
                      cursor: 'pointer',
                      fontSize: 20
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
              
              {/* User Dropdown */}
              {showUserDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '0 0 4px 4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <div
                        key={user._id || user.id}
                        onClick={() => handleUserSelect(user)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: '500' }}>{user.name}</div>
                          <div style={{ fontSize: 12, color: '#666' }}>
                           {user?.profile?.name} ,{user.email} ,{user.mobile && `• ${user.mobile}`}
                          </div>
                        </div>
                        <div style={{ fontSize: 12, color: '#4caf50' }}>
                          Select
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '12px', color: '#666', textAlign: 'center' }}>
                      No users found
                    </div>
                  )}
                </div>
              )}
            </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                  Customer Email *
                </label>
                <input
                  type="email"
                  name="customerEmail"
                  value={formData.customerEmail}
                  onChange={handleInputChange}
                  placeholder="customer@example.com"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${errors.customerEmail ? '#f44336' : '#ddd'}`,
                    borderRadius: 4,
                    fontSize: 14,
                    background: selectedUser ? '#f9f9f9' : 'white',
                    color: selectedUser ? '#666' : 'inherit'
                  }}
                />
                {errors.customerEmail && (
                  <div style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>
                    {errors.customerEmail}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                  Customer Phone
                </label>
                <input
                  type="tel"
                  name="customerPhone"
                  value={formData.customerPhone}
                  onChange={handleInputChange}
                  placeholder="9876543210"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${errors.customerPhone ? '#f44336' : '#ddd'}`,
                    borderRadius: 4,
                    fontSize: 14,
                    background: selectedUser ? '#f9f9f9' : 'white',
                    color: selectedUser ? '#666' : 'inherit'
                  }}
                />
                {errors.customerPhone && (
                  <div style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>
                    {errors.customerPhone}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                  GSTIN
                </label>
                <input
                  type="text"
                  name="customerGSTIN"
                  value={formData.customerGSTIN}
                  onChange={handleInputChange}
                  placeholder="22AAAAA0000A1Z5"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: `1px solid ${errors.customerGSTIN ? '#f44336' : '#ddd'}`,
                    borderRadius: 4,
                    fontSize: 14,
                    background: selectedUser ? '#f9f9f9' : 'white',
                    color: selectedUser ? '#666' : 'inherit'
                  }}
                />
                {errors.customerGSTIN && (
                  <div style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>
                    {errors.customerGSTIN}
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
                Customer Address
              </label>
              <textarea
                name="customerAddress"
                value={formData.customerAddress}
                onChange={handleInputChange}
                placeholder="Full address..."
                rows="3"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  fontSize: 14,
                  resize: 'vertical',
                  background: selectedUser ? '#f9f9f9' : 'white',
                  color: selectedUser ? '#666' : 'inherit'
                }}
              />
            </div>
          </div>

          {/* Invoice Items */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>Invoice Items *</h3>
              <button
                type="button"
                onClick={addItem}
                style={{
                  background: '#f0f0f0',
                  border: '1px solid #ddd',
                  borderRadius: 4,
                  padding: '6px 12px',
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                + Add Item
              </button>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: 12, fontWeight: '500' }}>Description *</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: 12, fontWeight: '500' }}>Quantity *</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: 12, fontWeight: '500' }}>Unit Price *</th>
                    
                    {/* Tax Type Columns */}
                    {taxTypeOptions.map(tax => (
                      <th key={tax.value} style={{ padding: '8px', textAlign: 'left', fontSize: 12, fontWeight: '500' }}>
                        {tax.label}
                      </th>
                    ))}
                    
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: 12, fontWeight: '500' }}>Total Tax</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: 12, fontWeight: '500' }}>Amount</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: 12, fontWeight: '500' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, itemIndex) => (
                    <tr key={itemIndex}>
                      {/* Description */}
                      <td style={{ padding: '8px' }}>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(itemIndex, 'description', e.target.value)}
                          placeholder="Item description"
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: `1px solid ${errors[`item_description_${itemIndex}`] ? '#f44336' : '#ddd'}`,
                            borderRadius: 4,
                            fontSize: 14
                          }}
                        />
                        {errors[`item_description_${itemIndex}`] && (
                          <div style={{ color: '#f44336', fontSize: 11, marginTop: 2 }}>
                            {errors[`item_description_${itemIndex}`]}
                          </div>
                        )}
                      </td>
                      
                      {/* Quantity */}
                      <td style={{ padding: '8px' }}>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(itemIndex, 'quantity', e.target.value)}
                          min="1"
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: `1px solid ${errors[`item_quantity_${itemIndex}`] ? '#f44336' : '#ddd'}`,
                            borderRadius: 4,
                            fontSize: 14
                          }}
                        />
                        {errors[`item_quantity_${itemIndex}`] && (
                          <div style={{ color: '#f44336', fontSize: 11, marginTop: 2 }}>
                            {errors[`item_quantity_${itemIndex}`]}
                          </div>
                        )}
                      </td>
                      
                      {/* Unit Price */}
                      <td style={{ padding: '8px' }}>
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(itemIndex, 'unitPrice', e.target.value)}
                          min="0"
                          step="0.01"
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: `1px solid ${errors[`item_unitPrice_${itemIndex}`] ? '#f44336' : '#ddd'}`,
                            borderRadius: 4,
                            fontSize: 14
                          }}
                        />
                        {errors[`item_unitPrice_${itemIndex}`] && (
                          <div style={{ color: '#f44336', fontSize: 11, marginTop: 2 }}>
                            {errors[`item_unitPrice_${itemIndex}`]}
                          </div>
                        )}
                      </td>
                      
                      {/* Tax Type Columns */}
                      {taxTypeOptions.map(tax => {
                        const isSelected = isTaxTypeSelected(itemIndex, tax.value);
                        const percentage = getTaxPercentage(itemIndex, tax.value);
                        
                        return (
                          <td key={tax.value} style={{ padding: '8px', verticalAlign: 'top' }}>
                            <div style={{ marginBottom: 4 }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleTaxTypeChange(itemIndex, tax.value)}
                                style={{ marginRight: 4 }}
                              />
                              <label style={{ fontSize: 12 }}>
                                {tax.label}
                              </label>
                            </div>
                            
                            {isSelected && (
                              <div>
                                <input
                                  type="number"
                                  value={percentage}
                                  onChange={(e) => handleTaxPercentageChange(itemIndex, tax.value, e.target.value)}
                                  placeholder="%"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  style={{
                                    width: '100%',
                                    padding: '6px 8px',
                                    border: `1px solid ${errors[`item_${itemIndex}_tax_${tax.value}`] ? '#f44336' : '#ddd'}`,
                                    borderRadius: 4,
                                    fontSize: 12
                                  }}
                                />
                                {errors[`item_${itemIndex}_tax_${tax.value}`] && (
                                  <div style={{ color: '#f44336', fontSize: 10, marginTop: 2 }}>
                                    {errors[`item_${itemIndex}_tax_${tax.value}`]}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                      
                      {/* Total Tax Percentage */}
                      <td style={{ padding: '8px', fontWeight: '500', textAlign: 'center' }}>
                        {calculateItemTaxPercentage(itemIndex).toFixed(2)}%
                      </td>
                      
                      {/* Item Amount */}
                      <td style={{ padding: '8px', fontWeight: '500' }}>
                        {formatCurrency(item.amount)}
                      </td>
                      
                      {/* Actions */}
                      <td style={{ padding: '8px' }}>
                        <button
                          type="button"
                          onClick={() => removeItem(itemIndex)}
                          disabled={formData.items.length === 1}
                          style={{
                            background: formData.items.length === 1 ? '#f5f5f5' : '#ffebee',
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            padding: '6px 8px',
                            fontSize: 12,
                            cursor: formData.items.length === 1 ? 'not-allowed' : 'pointer',
                            color: formData.items.length === 1 ? '#999' : '#f44336'
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals Section */}
          <div style={{ 
            padding: 16, 
            background: '#f8f9fa', 
            borderRadius: 4,
            marginBottom: 24 
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Subtotal:</strong> {formatCurrency(formData.subtotal)}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Tax:</strong> {formatCurrency(formData.tax)}
                </div>
              </div>
              <div style={{ fontSize: 20, fontWeight: 'bold' }}>
                Total: {formatCurrency(formData.total)}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: '500' }}>
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Additional notes or terms..."
              rows="3"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: 4,
                fontSize: 14,
                resize: 'vertical'
              }}
            />
          </div>

          {/* Form Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 24px',
                background: '#f5f5f5',
                border: '1px solid #ddd',
                borderRadius: 4,
                fontSize: 14,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                padding: '10px 24px',
                background: isSubmitting ? '#ccc' : '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                fontSize: 14,
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}